import { StringSubType } from "@evergis/api";
import React, { useCallback, useEffect, useState } from "react";
import { Column } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { TableFilter } from "../components/TableFilter";
import { getAttributePrefixIcon } from "../../../utils/getAttributePrefixIcon";
import { useTableFilters } from "../../../hooks/table/useTableFilters";
// eslint-disable-next-line max-len
import { ScalingFactor } from "../../../components/Dialogs/DialogComponents/Attributes/AttributeFormat/FormatSettingsOptions";
import { LayerInfo } from "../../../interfaces/resourceInfo";
import { TableFilterState } from "../types";

interface TableColumnsResponse {
  tableColumns: Column[];
  filters: Record<string, TableFilterState>;
  setTableFilters: (filters: Record<string, TableFilterState>) => void;
}

// eslint-disable-next-line max-lines-per-function
export const useTableColumns = (layerInfo: LayerInfo): TableColumnsResponse => {
  const { t } = useTranslation();
  const layerAttributes = layerInfo?.layerDefinition && layerInfo.layerDefinition.attributes;
  const [tableColumns, setTableColumns] = useState<Column[]>([]);
  const [filters, { setTableFilters }] = useTableFilters();

  const getNumberHeaderUnitsText = useCallback(
    (scalingFactor: ScalingFactor) => {
      switch (scalingFactor) {
        case ScalingFactor.Thousands:
          return `, ${t("thousands", { ns: "common" })}`;
        case ScalingFactor.Millions:
          return `, ${t("millions", { ns: "common" })}`;
        case ScalingFactor.Acres:
          return `, ${t("acres", { ns: "common" })}`;
        case ScalingFactor.Hectares:
          return `, ${t("hectare", { ns: "common" })}`;
        default:
          return "";
      }
    },
    [t],
  );

  useEffect(() => {
    if (!layerAttributes) {
      return;
    }

    setTableColumns(
      Object.entries(layerAttributes).reduce((acc, [key, value]) => {
        if (value.isDisplayed && key !== layerInfo?.geometryAttribute) {
          const { alias, stringFormat, type, subType } = value;
          const header = alias || key;
          const Header =
            stringFormat && stringFormat.scalingFactor
              ? `${header}${getNumberHeaderUnitsText(stringFormat.scalingFactor)}`
              : header;

          return [
            ...acc,
            {
              id: key,
              accessor: key,
              Header,
              prefix: getAttributePrefixIcon(key, type, subType),
              noLinkWrap: subType !== StringSubType.Image,
              filter: () => (
                <TableFilter
                  layerName={layerInfo?.name}
                  attributeType={type}
                  filters={filters}
                  setFilters={setTableFilters}
                  filterKey={key}
                  filterValue={value}
                />
              ),
            },
          ];
        }

        return acc;
      }, []),
    );
  }, [layerAttributes, filters, layerInfo?.geometryAttribute, layerInfo?.name]);

  return {
    tableColumns,
    filters,
    setTableFilters,
  };
};
